import { ReactNode, useId, useRef } from "react";
import { AlertCircle, CheckCircle2, Eraser, Eye, FilePlus, Loader2, RotateCw, Save, Square, Upload } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CsvImportPlan } from "@/lib/csvImportBatches";
import { CsvBatchImportState } from "./useCsvBatchImport";

interface Props<TResult, TPlan extends CsvImportPlan> {
  state: CsvBatchImportState<TResult, TPlan>;
  selectLabel: string;
  /** Texto sobre o arquivo lido (linhas e lotes). */
  describePlan: (plan: TPlan) => string;
  hasChanges: (result: TResult) => boolean;
  nothingToSaveMessage: string;
  renderResult: (result: TResult, fileName: string) => ReactNode;
}

/**
 * Arquivo, prévia, confirmação, andamento dos lotes, cancelamento, retomada e resultado de uma importação CSV em
 * lotes. Só aparecem os botões da etapa atual: selecionar → pré-visualizar → confirmar, com Cancelar durante a prévia
 * (a gravação não tem Cancelar) e Limpar para recomeçar.
 */
export function CsvBatchImportControls<TResult, TPlan extends CsvImportPlan>({
  state,
  selectLabel,
  describePlan,
  hasChanges,
  nothingToSaveMessage,
  renderResult,
}: Props<TResult, TPlan>) {
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { file, plan, fileError, run, preview, saved, isRunning } = state;
  const previewHasChanges = !!preview && hasChanges(preview);
  const totalBatches = run?.plan.batches.length ?? 0;
  const stopped = run?.status === "failed" || run?.status === "cancelled";

  // O input guarda o arquivo escolhido; zerar permite escolher o mesmo arquivo de novo depois de limpar.
  const clear = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    state.clear();
  };

  const step = isRunning
    ? run?.mode === "preview"
      ? "Gerando a prévia. Nada é gravado nesta etapa."
      : "Gravando a importação, lote a lote. Aguarde até o fim."
    : stopped
      ? run?.mode === "confirm"
        ? "Importação parada. Retome para continuar do lote em que parou, sem duplicar nada, ou limpe para recomeçar."
        : "Prévia parada. Retome para continuar ou limpe para recomeçar."
      : saved
        ? "Importação concluída. Para importar outro arquivo, clique em Nova importação."
        : preview
          ? previewHasChanges
            ? "Confira a prévia abaixo e clique em Confirmar importação para gravar."
            : "A prévia não tem nada a gravar. Escolha outro arquivo ou limpe."
          : file
            ? "Gere a prévia para conferir o que vai mudar antes de gravar."
            : "Selecione o arquivo CSV.";

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium" aria-live="polite">{step}</p>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        {!isRunning && !saved && (
          <label
            htmlFor={fileInputId}
            className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm hover:bg-muted/50 focus-within:ring-2 focus-within:ring-ring"
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            <span>{file ? `${file.name} (trocar arquivo)` : selectLabel}</span>
            <input
              ref={fileInputRef}
              id={fileInputId}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(event) => void state.selectFile(event.target.files?.[0] ?? null)}
            />
          </label>
        )}

        <div className="flex flex-wrap gap-2">
          {/* Só a prévia pode ser cancelada: a gravação vai até o fim (se falhar, para e oferece Retomar). */}
          {isRunning && run?.mode === "preview" && (
            <Button variant="outline" onClick={state.cancel}>
              <Square className="mr-2 h-4 w-4" aria-hidden="true" />
              Cancelar
            </Button>
          )}

          {!isRunning && stopped && (
            <Button onClick={state.resume}>
              <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
              {run!.nextBatch < totalBatches ? `Retomar do lote ${run!.nextBatch + 1}` : "Tentar de novo"}
            </Button>
          )}

          {!isRunning && !stopped && plan && !preview && !saved && (
            <Button onClick={() => state.start("preview")}>
              <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
              Pré-visualizar
            </Button>
          )}

          {!isRunning && !stopped && previewHasChanges && (
            <Button onClick={() => state.start("confirm")}>
              <Save className="mr-2 h-4 w-4" aria-hidden="true" />
              Confirmar importação
            </Button>
          )}

          {!isRunning && saved && (
            <Button variant="outline" onClick={clear}>
              <FilePlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Nova importação
            </Button>
          )}

          {!isRunning && !saved && (file || fileError || preview || stopped) && (
            <Button variant="ghost" onClick={clear}>
              <Eraser className="mr-2 h-4 w-4" aria-hidden="true" />
              Limpar
            </Button>
          )}
        </div>
      </div>
      {plan && !saved && <p className="text-sm text-muted-foreground">{describePlan(plan)}</p>}

      {fileError && (
        <p className="text-sm text-destructive" role="alert">{fileError}</p>
      )}

      {run && (
        <section aria-label="Andamento da importação" className="space-y-2" aria-live="polite">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              {isRunning && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {run.mode === "preview" ? "Prévia" : "Gravação"}: lote{" "}
              {Math.min(run.nextBatch + (isRunning ? 1 : 0), totalBatches)} de {totalBatches}
            </span>
            <span className="text-muted-foreground">{run.sentLines} linha(s) enviada(s)</span>
          </div>
          <Progress value={totalBatches === 0 ? 0 : (run.nextBatch / totalBatches) * 100} />
        </section>
      )}
      {run?.status === "failed" && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{run.mode === "preview" ? "Prévia interrompida" : "Importação interrompida"}</AlertTitle>
          <AlertDescription>
            {run.error}
            {run.mode === "confirm" && run.nextBatch > 0 && " Os lotes anteriores já foram gravados."}
          </AlertDescription>
        </Alert>
      )}
      {run?.status === "cancelled" && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Prévia cancelada</AlertTitle>
          <AlertDescription>
            Parou no lote {Math.min(run.nextBatch + 1, totalBatches)} de {totalBatches}. Nada foi gravado.
          </AlertDescription>
        </Alert>
      )}
      {preview && !previewHasChanges && !run && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{nothingToSaveMessage}</AlertDescription>
        </Alert>
      )}

      {preview && file && (
        <section aria-label="Prévia da importação" className="space-y-2">
          <h2 className="font-semibold">Prévia (nada foi gravado)</h2>
          {renderResult(preview, file.name)}
        </section>
      )}
      {saved && file && (
        <section aria-label="Resumo da importação" className="space-y-2">
          <h2 className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            Importação gravada
          </h2>
          {renderResult(saved, file.name)}
        </section>
      )}
    </div>
  );
}
